import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Palette,
  Plus,
  Edit2,
  Trash2,
  Copy,
  Check,
  X,
  Search,
  Star,
  Home,
  ChefHat,
  Bath,
  BedDouble,
  Briefcase,
  Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Room type icons
const roomIcons = {
  'kitchen': ChefHat,
  'bathroom': Bath,
  'bedroom': BedDouble,
  'living room': Home,
  'office': Briefcase,
  'default': Sparkles
};

export const FinishLibrary = () => {
  const { api } = useAuth();
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null);
  const [selectedRoomFilter, setSelectedRoomFilter] = useState('all');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    room_type: 'kitchen',
    style: 'modern',
    description: '',
    is_favorite: false,
    finishes: {
      flooring: '',
      flooring_color: '',
      wall_color: '',
      wall_finish: '',
      ceiling: '',
      lighting_type: '',
      countertops: '',
      cabinets: '',
      cabinet_hardware: '',
      backsplash: '',
      trim_color: '',
      trim_style: '',
      fixtures_finish: ''
    }
  });

  useEffect(() => {
    fetchPresets();
  }, []);

  const fetchPresets = async () => {
    try {
      const response = await api.get('/floorplans/finish-library');
      setPresets(response.data);
    } catch (error) {
      console.error('Error fetching presets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPreset) {
        await api.patch(`/floorplans/finish-library/${editingPreset.id}`, formData);
        toast.success('Preset updated successfully');
      } else {
        await api.post('/floorplans/finish-library', formData);
        toast.success('Preset created successfully');
      }
      fetchPresets();
      setShowCreateModal(false);
      setEditingPreset(null);
      resetForm();
    } catch (error) {
      toast.error('Failed to save preset');
    }
  };

  const handleDelete = async (presetId) => {
    if (!window.confirm('Are you sure you want to delete this preset?')) return;
    try {
      await api.delete(`/floorplans/finish-library/${presetId}`);
      toast.success('Preset deleted');
      fetchPresets();
    } catch (error) {
      toast.error('Failed to delete preset');
    }
  };

  const handleEdit = (preset) => {
    setFormData({
      name: preset.name,
      room_type: preset.room_type,
      style: preset.style,
      description: preset.description || '',
      is_favorite: preset.is_favorite || false,
      finishes: preset.finishes || {}
    });
    setEditingPreset(preset);
    setShowCreateModal(true);
  };

  const handleDuplicate = async (preset) => {
    try {
      const duplicateData = {
        ...preset,
        name: `${preset.name} (Copy)`,
        is_favorite: false
      };
      delete duplicateData.id;
      delete duplicateData.created_at;
      
      await api.post('/floorplans/finish-library', duplicateData);
      toast.success('Preset duplicated');
      fetchPresets();
    } catch (error) {
      toast.error('Failed to duplicate preset');
    }
  };

  const toggleFavorite = async (preset) => {
    try {
      await api.patch(`/floorplans/finish-library/${preset.id}`, {
        ...preset,
        is_favorite: !preset.is_favorite
      });
      fetchPresets();
    } catch (error) {
      toast.error('Failed to update favorite');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      room_type: 'kitchen',
      style: 'modern',
      description: '',
      is_favorite: false,
      finishes: {
        flooring: '',
        flooring_color: '',
        wall_color: '',
        wall_finish: '',
        ceiling: '',
        lighting_type: '',
        countertops: '',
        cabinets: '',
        cabinet_hardware: '',
        backsplash: '',
        trim_color: '',
        trim_style: '',
        fixtures_finish: ''
      }
    });
  };

  const filteredPresets = presets
    .filter(preset => 
      (selectedRoomFilter === 'all' || preset.room_type === selectedRoomFilter) &&
      (searchTerm === '' || 
        preset.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        preset.description?.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .sort((a, b) => {
      if (a.is_favorite && !b.is_favorite) return -1;
      if (!a.is_favorite && b.is_favorite) return 1;
      return new Date(b.created_at) - new Date(a.created_at);
    });

  const roomTypes = ['all', 'kitchen', 'bathroom', 'bedroom', 'living room', 'office'];

  const getRoomIcon = (roomType) => {
    const Icon = roomIcons[roomType] || roomIcons.default;
    return Icon;
  };

  return (
    <div className="space-y-6" data-testid="finish-library-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="font-heading text-3xl flex items-center gap-3">
            <Palette className="w-8 h-8 text-[#C6A87C]" />
            Finish Library
          </h1>
          <p className="text-gray-500 mt-1">
            Save and reuse your favorite finish combinations for 3D renders
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setEditingPreset(null);
            setShowCreateModal(true);
          }}
          className="flex items-center gap-2 px-6 py-3 bg-[#C6A87C] text-white font-semibold hover:bg-[#B09265] transition-colors"
          data-testid="create-preset-btn"
        >
          <Plus className="w-5 h-5" />
          New Preset
        </button>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search presets..."
            className="w-full pl-12 pr-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto">
          {roomTypes.map((room) => {
            const Icon = getRoomIcon(room);
            return (
              <button
                key={room}
                onClick={() => setSelectedRoomFilter(room)}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium whitespace-nowrap capitalize transition-colors ${
                  selectedRoomFilter === room
                    ? 'bg-[#C6A87C] text-white'
                    : 'bg-white border border-gray-200 text-gray-600 hover:border-[#C6A87C]'
                }`}
              >
                <Icon className="w-4 h-4" />
                {room === 'all' ? 'All Rooms' : room}
              </button>
            );
          })}
        </div>
      </div>

      {/* Presets Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      ) : filteredPresets.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-100">
          <Palette className="w-16 h-16 text-gray-200 mx-auto mb-4" />
          <h3 className="font-heading text-xl text-gray-400 mb-2">No Presets Found</h3>
          <p className="text-gray-400 mb-6">Create your first finish preset to get started</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-[#C6A87C] text-white font-semibold hover:bg-[#B09265] transition-colors"
          >
            Create Preset
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPresets.map((preset) => {
            const RoomIcon = getRoomIcon(preset.room_type);
            return (
              <motion.div
                key={preset.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white border border-gray-100 hover:border-[#C6A87C] transition-colors"
              >
                {/* Card Header */}
                <div className="p-4 border-b border-gray-100">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#C6A87C]/10 rounded-full flex items-center justify-center">
                        <RoomIcon className="w-5 h-5 text-[#C6A87C]" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{preset.name}</h3>
                        <p className="text-sm text-gray-500 capitalize">{preset.room_type} • {preset.style}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleFavorite(preset)}
                      className={`p-1 transition-colors ${preset.is_favorite ? 'text-yellow-500' : 'text-gray-300 hover:text-yellow-500'}`}
                    >
                      <Star className={`w-5 h-5 ${preset.is_favorite ? 'fill-current' : ''}`} />
                    </button>
                  </div>
                  {preset.description && (
                    <p className="text-sm text-gray-500 mt-2">{preset.description}</p>
                  )}
                </div>

                {/* Finishes Preview */}
                <div className="p-4 space-y-2 text-sm">
                  {preset.finishes?.flooring && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Flooring:</span>
                      <span className="font-medium">{preset.finishes.flooring}</span>
                    </div>
                  )}
                  {preset.finishes?.wall_color && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Walls:</span>
                      <span className="font-medium">{preset.finishes.wall_color}</span>
                    </div>
                  )}
                  {preset.finishes?.cabinets && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Cabinets:</span>
                      <span className="font-medium">{preset.finishes.cabinets}</span>
                    </div>
                  )}
                  {preset.finishes?.countertops && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Counters:</span>
                      <span className="font-medium">{preset.finishes.countertops}</span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="p-3 bg-gray-50 flex items-center justify-end gap-2">
                  <button
                    onClick={() => handleDuplicate(preset)}
                    className="p-2 text-gray-400 hover:text-[#C6A87C] transition-colors"
                    title="Duplicate"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleEdit(preset)}
                    className="p-2 text-gray-400 hover:text-[#C6A87C] transition-colors"
                    title="Edit"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(preset.id)}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto py-4"
            onClick={() => {
              setShowCreateModal(false);
              setEditingPreset(null);
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white w-full max-w-2xl m-4 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
                <h2 className="font-heading text-xl">
                  {editingPreset ? 'Edit Preset' : 'Create New Preset'}
                </h2>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingPreset(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Preset Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                      placeholder="e.g., Modern Farmhouse Kitchen"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Room Type</label>
                    <select
                      value={formData.room_type}
                      onChange={(e) => setFormData({ ...formData, room_type: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                    >
                      <option value="kitchen">Kitchen</option>
                      <option value="bathroom">Bathroom</option>
                      <option value="bedroom">Bedroom</option>
                      <option value="living room">Living Room</option>
                      <option value="office">Office</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Style</label>
                    <select
                      value={formData.style}
                      onChange={(e) => setFormData({ ...formData, style: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                    >
                      <option value="modern">Modern</option>
                      <option value="contemporary">Contemporary</option>
                      <option value="traditional">Traditional</option>
                      <option value="farmhouse">Farmhouse</option>
                      <option value="industrial">Industrial</option>
                      <option value="coastal">Coastal</option>
                      <option value="minimalist">Minimalist</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={2}
                      className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                      placeholder="Optional description..."
                    />
                  </div>
                </div>

                {/* Finishes */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4">Finish Selections</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Flooring</label>
                      <select
                        value={formData.finishes.flooring}
                        onChange={(e) => setFormData({
                          ...formData,
                          finishes: { ...formData.finishes, flooring: e.target.value }
                        })}
                        className="w-full px-3 py-2 border border-gray-200 text-sm focus:border-[#C6A87C] focus:outline-none"
                      >
                        <option value="">Select...</option>
                        <option value="hardwood oak">Hardwood Oak</option>
                        <option value="hardwood walnut">Hardwood Walnut</option>
                        <option value="porcelain tile">Porcelain Tile</option>
                        <option value="marble tile">Marble Tile</option>
                        <option value="luxury vinyl plank">Luxury Vinyl Plank</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Wall Color</label>
                      <select
                        value={formData.finishes.wall_color}
                        onChange={(e) => setFormData({
                          ...formData,
                          finishes: { ...formData.finishes, wall_color: e.target.value }
                        })}
                        className="w-full px-3 py-2 border border-gray-200 text-sm focus:border-[#C6A87C] focus:outline-none"
                      >
                        <option value="">Select...</option>
                        <option value="pure white">Pure White</option>
                        <option value="swiss coffee">Swiss Coffee</option>
                        <option value="agreeable gray">Agreeable Gray</option>
                        <option value="sage green">Sage Green</option>
                        <option value="navy blue">Navy Blue</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Cabinets</label>
                      <select
                        value={formData.finishes.cabinets}
                        onChange={(e) => setFormData({
                          ...formData,
                          finishes: { ...formData.finishes, cabinets: e.target.value }
                        })}
                        className="w-full px-3 py-2 border border-gray-200 text-sm focus:border-[#C6A87C] focus:outline-none"
                      >
                        <option value="">Select...</option>
                        <option value="white shaker">White Shaker</option>
                        <option value="gray shaker">Gray Shaker</option>
                        <option value="natural wood">Natural Wood</option>
                        <option value="dark espresso">Dark Espresso</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Countertops</label>
                      <select
                        value={formData.finishes.countertops}
                        onChange={(e) => setFormData({
                          ...formData,
                          finishes: { ...formData.finishes, countertops: e.target.value }
                        })}
                        className="w-full px-3 py-2 border border-gray-200 text-sm focus:border-[#C6A87C] focus:outline-none"
                      >
                        <option value="">Select...</option>
                        <option value="white quartz">White Quartz</option>
                        <option value="calacatta quartz">Calacatta Quartz</option>
                        <option value="black granite">Black Granite</option>
                        <option value="butcher block">Butcher Block</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Fixtures Finish</label>
                      <select
                        value={formData.finishes.fixtures_finish}
                        onChange={(e) => setFormData({
                          ...formData,
                          finishes: { ...formData.finishes, fixtures_finish: e.target.value }
                        })}
                        className="w-full px-3 py-2 border border-gray-200 text-sm focus:border-[#C6A87C] focus:outline-none"
                      >
                        <option value="">Select...</option>
                        <option value="brushed nickel">Brushed Nickel</option>
                        <option value="matte black">Matte Black</option>
                        <option value="polished chrome">Polished Chrome</option>
                        <option value="brushed gold">Brushed Gold</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Lighting</label>
                      <select
                        value={formData.finishes.lighting_type}
                        onChange={(e) => setFormData({
                          ...formData,
                          finishes: { ...formData.finishes, lighting_type: e.target.value }
                        })}
                        className="w-full px-3 py-2 border border-gray-200 text-sm focus:border-[#C6A87C] focus:outline-none"
                      >
                        <option value="">Select...</option>
                        <option value="recessed LED">Recessed LED</option>
                        <option value="pendant lights">Pendant Lights</option>
                        <option value="chandelier">Chandelier</option>
                        <option value="track lighting">Track Lighting</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Favorite Toggle */}
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_favorite}
                    onChange={(e) => setFormData({ ...formData, is_favorite: e.target.checked })}
                    className="w-5 h-5 text-[#C6A87C] border-gray-300 rounded focus:ring-[#C6A87C]"
                  />
                  <Star className="w-5 h-5 text-yellow-500" />
                  <span>Mark as favorite</span>
                </label>

                {/* Submit */}
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setEditingPreset(null);
                    }}
                    className="px-6 py-3 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 px-6 py-3 bg-[#C6A87C] text-white font-semibold hover:bg-[#B09265] transition-colors"
                  >
                    <Check className="w-5 h-5" />
                    {editingPreset ? 'Save Changes' : 'Create Preset'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
